import {
  getAuth,
  signInWithCustomToken,
  UserCredential,
  AdditionalUserInfo,
} from 'firebase/auth';
import {
  initialState,
  update,
  store,
  selectedApp,
  subscribe,
  IGlobalState,
  type AppUser,
} from '../../store';
import {
  onUserApplicationUpdate,
  signInWithGoogle,
  signInWithTwitter,
  signInWithGithub,
  createPasswordUser,
  updateProfile,
  updateOrCreateCurrentUserData,
  createUserData,
  onUserAuth,
  getUserData,
  updateOrCreateUserData,
  getUserApplicationsData,
  onUserUpdate,
  onGlobalFeatureFlagsUpdate,
  ensureCurrentUserHasEmailClaim,
  getIdToken,
  getOwnAcceptedUsers,
  onAcceptedUsersUpdate,
  onInvitedUsersUpdate,
  callFirebaseFunction,
  signOut,
  type ISignInUser,
} from '../firebase';
import {
  getAuthErrorMessage,
  getBlockingFunctionError,
  toError,
} from '../util/errors';
import { isAccessDenied } from '../util/accessErrors';
import { getAccountDisplayName } from '../util/accountDisplayName';
import { addApps } from './application';
import { Modal } from 'antd';
import * as Sentry from '@sentry/react';
import type { IAuthUser } from '../types';
import {
  track,
  reset as analyticsReset,
  identify as analyticsIdentify,
} from '../util/analytics';
import { authCancelledCodes, defaultUser } from '../constants';
import { IPasswordAccountForm } from '../../components/account/PasswordAccountForm';
import { resetBuildSubscriptions } from './builds';
import { history } from '../util';
import { getAuthRedirect, ssoLoginPath } from '../util/authRedirect';
import {
  IApp,
  IUser,
  restrictedSessionReason,
  SSO_SESSION_ENDS_CLAIM,
  UserIHaveAcceptedInviteFrom,
  UserIHaveSentInviteTo,
} from '@todesktop/shared';
import { getCollection } from '../firebase/common';
import { consumeLeftTeam } from './teamMembership';

let appStoreUnsubscribe: (() => void) | undefined;
let firebaseUnsubscribe: (() => void) | undefined;
let firebaseUserUnsubscribe: (() => void) | undefined;
let acceptedUsersUnsubscribe: (() => void) | undefined;
let invitedUsersUnsubscribe: (() => void) | undefined;
let authUnsubscribe: (() => void) | undefined;
let featureFlagsUnsubscribe: (() => void) | undefined;

export const resetAllState = () => {
  resetBuildSubscriptions();

  if (firebaseUserUnsubscribe) firebaseUserUnsubscribe();
  if (firebaseUnsubscribe) firebaseUnsubscribe();
  if (acceptedUsersUnsubscribe) acceptedUsersUnsubscribe();
  if (invitedUsersUnsubscribe) invitedUsersUnsubscribe();
  if (appStoreUnsubscribe) appStoreUnsubscribe();
  if (featureFlagsUnsubscribe) featureFlagsUnsubscribe();

  history.push('/');
  update((draft) => {
    Object.keys(initialState).forEach((key) => {
      (draft as unknown as Record<string, unknown>)[key] = (
        initialState as unknown as Record<string, unknown>
      )[key];
    });
  });
};

export const subscribeApplicationUpdates = (userId: string, appId: string) => {
  if (firebaseUnsubscribe) {
    firebaseUnsubscribe();
  }

  let active = true;
  const removeUnavailableApp = () => {
    if (!active) return;
    update((draft) => {
      draft.apps = draft.apps.filter((app) => app.id !== appId);
      delete draft.appToUser[appId];
      if (draft.selectedApp === appId) {
        draft.selectedApp = null;
        draft.uiState = null;
      }
    });
  };

  const unsubscribe = onUserApplicationUpdate(
    userId,
    appId,
    (updatedApp, snapshot) => {
      if (!active) return;
      if (!updatedApp) {
        if (snapshot?.metadata.fromCache === false) removeUnavailableApp();
        return;
      }
      update((draft) => {
        const app = selectedApp(draft);
        if (!app || app.id !== appId) return;

        // update the outdated values in app
        Object.assign(app, updatedApp);
        if (app.meta?.latestReleaseBuildId) {
          draft.buildState.releasedBuildId = app.meta.latestReleaseBuildId;
        }

        /**
         * Delete keys that are in app, but not in updatedApp. Nested keys are not deleted.
         *
         * fromCache === false gives us confidence that a full up-to-date copy of the data is being
         * returned from firestore so that we don't accidentally delete keys from the app in state
         */
        if (
          snapshot &&
          snapshot.metadata.fromCache === false &&
          updatedApp.id
        ) {
          const incomingKeys = Object.keys(updatedApp);
          Object.keys(app)
            .filter((k) => !incomingKeys.includes(k))
            .forEach(
              (k) => delete (app as unknown as Record<string, unknown>)[k],
            );
        }
      });
    },
    (error) => {
      if (!active) return;
      if (isAccessDenied(error)) {
        removeUnavailableApp();
      } else {
        console.error('Unable to update application', error);
      }
    },
  );
  firebaseUnsubscribe = () => {
    active = false;
    unsubscribe?.();
  };
};

let ssoSessionTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Signs out when a team's session length runs out (the session's
 * ssoSessionEnds, in seconds): rules and functions refuse the session from
 * then on, so the dashboard would only show errors.
 */
function scheduleSsoSessionEnd(ends: unknown) {
  clearTimeout(ssoSessionTimer);
  if (typeof ends !== 'number') return;
  // setTimeout's longest delay is about 24 days; sessions are shorter
  const delay = Math.min(ends * 1000 - Date.now(), 2 ** 31 - 1);
  ssoSessionTimer = setTimeout(() => void endSsoSession(), Math.max(delay, 0));
}

/** The session's token claims, or undefined when they can't be read. */
async function readSessionClaims(): Promise<
  Record<string, unknown> | undefined
> {
  try {
    return (await getIdToken()).claims;
  } catch {
    return undefined;
  }
}

/** Signs out a session past its team's length, saying why. */
async function endSsoSession() {
  if (!getAuth().currentUser) return;
  await signOut();
  Modal.info({
    title: 'Signed out',
    content:
      'Your team signs people out after a while. Sign in again to carry on.',
  });
}

function showModalError(message: string, title = 'Authentication Error') {
  Modal.error({
    title,
    content: message,
  });
}

// Titles for the reasons our blocking functions give for refusing a sign-up
const authErrorTitleByReason: Record<string, string> = {
  'email-conflict': 'Account already exists',
  'missing-email': 'Email address required',
  'sso-domain-mismatch': 'Can’t sign in with this identity provider',
  'sso-required': 'Your team signs in with single sign-on',
  'sso-unverified-email': 'Email address not verified',
};

const MISSING_AUTH_EMAIL_ERROR =
  'Your sign-in provider did not return an email address. Please sign in with an account that has a visible email.';

const catchAuthError = (e: unknown, provider: string) => {
  const { code, message } = toError<{ code?: string }>(e);
  track({
    event: 'Error On Signing Up',
    properties: {
      provider,
      error: message,
    },
  });
  if (!code || !authCancelledCodes.includes(code)) {
    const refusal = getBlockingFunctionError(e);
    const reason = refusal?.reason;
    const domain = refusal?.details?.domain;
    if (reason === 'sso-required' && typeof domain === 'string') {
      Modal.confirm({
        cancelText: 'Close',
        content: refusal?.message,
        okText: 'Continue with single sign-on',
        onOk: () => {
          history.push(
            ssoLoginPath({
              domain,
              redirectTo: getAuthRedirect(
                window.location.search,
                window.location.hash,
              ),
            }),
          );
        },
        title: authErrorTitleByReason[reason],
      });
      return;
    }
    showModalError(
      getAuthErrorMessage(e),
      reason ? authErrorTitleByReason[reason] : undefined,
    );
  }
};

export const userSignInWithGoogle = async () => {
  return signInWithGoogle();
};

export const userSignInWithTwitter = async () => {
  return signInWithTwitter();
};

export const userSignInWithGithub = async () => {
  return signInWithGithub();
};

type OAuthProvider = 'google' | 'twitter' | 'github';

const handleOAuthAuth = async ({
  isLogin,
  provider,
  signIn,
  onAuthCb,
}: {
  isLogin: boolean;
  provider: OAuthProvider;
  signIn: () => Promise<ISignInUser>;
  onAuthCb?: () => void;
}) => {
  if (!isLogin) {
    track({
      event: 'Started Signing Up',
      properties: { provider },
    });
  }

  const user = await signIn();

  if (!isLogin || user.isNewUser) {
    track({ event: 'Signed Up', properties: { provider } });
  }

  if (onAuthCb) onAuthCb();
};

export const onGoogleAuth =
  (onAuthCb?: () => void) =>
  async (isLogin = true) => {
    try {
      await handleOAuthAuth({
        isLogin,
        provider: 'google',
        signIn: userSignInWithGoogle,
        onAuthCb,
      });
    } catch (e) {
      catchAuthError(e, 'google');
    }
  };

export const onTwitterAuth =
  (onAuthCb?: () => void) =>
  async (isLogin = true) => {
    try {
      await handleOAuthAuth({
        isLogin,
        provider: 'twitter',
        signIn: userSignInWithTwitter,
        onAuthCb,
      });
    } catch (e) {
      catchAuthError(e, 'twitter');
    }
  };

export const onGithubAuth =
  (onAuthCb?: () => void) =>
  async (isLogin = true) => {
    try {
      await handleOAuthAuth({
        isLogin,
        provider: 'github',
        signIn: userSignInWithGithub,
        onAuthCb,
      });
    } catch (e) {
      catchAuthError(e, 'github');
    }
  };

export const onPasswordSignup =
  (callback?: () => void) =>
  async ({ email, password, firstName, lastName }: IPasswordAccountForm) => {
    const authCredential = await createPasswordUser(email, password).catch(
      (e: unknown) => {
        throw Object.assign(new Error(getAuthErrorMessage(e)), {
          code: toError<{ code?: string }>(e).code,
        });
      },
    );
    track({ event: 'Signed Up', properties: { provider: 'password' } });
    // TODO: Populate profile by trigger (not manually)
    await updateProfile({
      displayName: `${firstName} ${lastName}`,
    });
    await updateOrCreateCurrentUserData(
      {
        firstName,
        lastName,
        email,
      },
      {
        isNewUser:
          (
            authCredential as UserCredential & {
              additionalUserInfo?: AdditionalUserInfo;
            }
          ).additionalUserInfo?.isNewUser ?? true,
      },
    );
    if (callback) callback();
  };

const createUser = async (user: IUser) => {
  const result = {
    ...defaultUser,
    ...user,
    authInfo: {
      ...defaultUser.authInfo,
      ...user.authInfo,
    },
  };

  if (!result.email) {
    throw new Error(MISSING_AUTH_EMAIL_ERROR);
  }

  await createUserData(result.id, result, { isNewUser: true });
  return result;
};

const isOfflineError = (error: unknown) => {
  const { code, message } = toError<{ code?: string }>(error);
  return (
    code === 'unavailable' ||
    code === 'auth/network-request-failed' ||
    message.includes('client is offline')
  );
};

function handleAuthInitError(error: unknown) {
  const offline = isOfflineError(error);
  if (offline) {
    console.warn('Failed to load account data while offline:', error);
  } else {
    console.error('Failed to load account data:', error);
    Sentry.captureException(error);
  }
  Modal.error({
    title: 'Couldn’t load your account',
    content: offline
      ? 'You appear to be offline. Check your connection and try again.'
      : 'Something went wrong while loading your account. Please try again.',
    okText: 'Try again',
    onOk: () => window.location.reload(),
  });
}

/**
 * Starts reading the person's own apps, the teams they belong to and each
 * team's apps, without waiting for one before the next.
 */
const loadAccountsAndApps = (userId: string) => {
  const ownApps = getUserApplicationsData(userId);
  const teams = getCollection<UserIHaveAcceptedInviteFrom>(
    getOwnAcceptedUsers(userId),
  ).then((acceptedUsers) => {
    const appsByAccount: Record<string, Promise<IApp[]>> = {};
    acceptedUsers.forEach(({ id }) => {
      appsByAccount[id] = getUserApplicationsData(id);
      // Rejections are handled where the apps are awaited
      appsByAccount[id].catch(() => undefined);
    });
    return { acceptedUsers, appsByAccount };
  });
  // Handled where they're awaited; an earlier return leaves them unawaited
  ownApps.catch(() => undefined);
  teams.catch(() => undefined);
  return { ownApps, teams };
};

let lastUser: IUser | undefined;
export const initializeOnUserAuth = () => {
  const handleUserAuth = async (authUser: IAuthUser | undefined) => {
    const hasLoggedOut = lastUser && authUser === undefined;
    const hasChangedUser = lastUser && authUser && lastUser.id !== authUser.id;
    if (hasLoggedOut || hasChangedUser) {
      resetAllState();
      analyticsReset();
    }
    lastUser = authUser;
    if (authUser) {
      // Reading the account's apps and teams only needs the uid, so start it
      // alongside the user document instead of after it
      const appsRequest = loadAccountsAndApps(authUser.id);
      const userRequest = getUserData(authUser.id);
      // A session past its team's length (e.g. coming back the next day)
      // can't read anything: sign out instead of failing to load
      if (
        restrictedSessionReason(await readSessionClaims()) ===
        'sso-session-ended'
      ) {
        userRequest.catch(() => undefined);
        if (lastUser === authUser) await endSsoSession();
        return;
      }
      let user = await userRequest;
      if (lastUser !== authUser) return;
      let resolvedEmail = authUser.email || user?.email || '';

      if (!resolvedEmail && !user) {
        // signInWithPopup and onAuthStateChanged race; give Firestore one quick retry
        await new Promise((resolve) => setTimeout(resolve, 250));
        user = await getUserData(authUser.id);
        if (lastUser !== authUser) return;
        resolvedEmail = user?.email || '';
      }

      if (!resolvedEmail) {
        showModalError(MISSING_AUTH_EMAIL_ERROR);
        await signOut().catch(() => undefined);
        return;
      }

      if (!user) {
        user = await createUser({ ...authUser, email: resolvedEmail });
      } else if (!user.email) {
        await updateOrCreateUserData(
          authUser.id,
          { email: resolvedEmail },
          { isNewUser: false },
        );
        user.email = resolvedEmail;
      }
      if (lastUser !== authUser) return;
      if (!user.id) {
        // Occasionally there was an earlier error where the id isn't populated
        console.warn('Repopulating user ID');
        await updateOrCreateUserData(
          authUser.id,
          { id: authUser.id },
          { isNewUser: false },
        );
        user.id = authUser.id;
      }

      if (lastUser !== authUser) return;
      const owner = {
        id: user.id,
        label: getAccountDisplayName(user),
        isOwner: true,
      };
      update((draft) => {
        draft.user = user;
        if (user.isOldAccountThatNeedsNewPassword) {
          draft.uiState = 'reset-password-on-upgraded-account';
        }
      });

      const isCurrentAuthSession = () =>
        lastUser === authUser && getAuth().currentUser?.uid === authUser.id;

      void (async () => {
        if (!isCurrentAuthSession()) return;
        let idTokenResult = await ensureCurrentUserHasEmailClaim(resolvedEmail);
        if (!isCurrentAuthSession()) return;
        idTokenResult ||= await getIdToken();

        // Token refresh can outlive logout or an account switch.
        if (!isCurrentAuthSession()) return;

        // Generate Intercom JWT for secure messenger identity
        let intercomJwt: string | undefined;
        try {
          const generateIntercomJwt = callFirebaseFunction(
            'generateIntercomJwt',
          );
          const { data } = await generateIntercomJwt({
            name:
              `${authUser.firstName} ${authUser.lastName}`.trim() || undefined,
          });
          intercomJwt = (data as { token: string })?.token;
        } catch (err) {
          if (!isCurrentAuthSession()) return;
          console.error('Failed to generate Intercom JWT:', err);
        }

        // Never restore an identity (or claims) after its session has ended.
        if (!isCurrentAuthSession()) return;
        analyticsIdentify(
          authUser.id,
          idTokenResult,
          {
            email: user.email || authUser.email,
            firstName: authUser.firstName,
            lastName: authUser.lastName,
          },
          intercomJwt,
        );

        if (idTokenResult.claims.admin) {
          update((draft) => {
            if (draft.user) {
              draft.user.isAdmin = true;
            }
          });
        }

        if (idTokenResult.claims.impersonator) {
          update((draft) => {
            if (draft.user) {
              draft.user.isImpersonator = true;
            }
          });
        }

        scheduleSsoSessionEnd(idTokenResult.claims[SSO_SESSION_ENDS_CLAIM]);
      })().catch((err) => {
        if (isCurrentAuthSession()) {
          console.error('Failed to initialize authenticated identity:', err);
        }
      });

      const isCurrentUser = () =>
        lastUser === authUser && store.getRawState().user?.id === user.id;
      const subscribeToApp = (appId: string) => {
        const state = store.getRawState();
        if (!isCurrentUser() || state.selectedApp !== appId) return;
        subscribeApplicationUpdates(
          state.appToUser[appId]?.id || user.id,
          appId,
        );
      };
      const currentAppId = store.getRawState().selectedApp;
      if (currentAppId) subscribeToApp(currentAppId);

      // Accounts whose apps are loaded, so the accepted users listener's first
      // snapshot doesn't read them all again
      const accountsWithApps = new Set<string>();
      const addAccountApps = async (
        account: AppUser,
        apps: Promise<IApp[]>,
      ) => {
        const fetchedApps = await apps;
        if (lastUser !== authUser) return;
        addApps(fetchedApps, account);
        accountsWithApps.add(account.id);
        update((draft) => {
          draft.accountAppsLoaded[account.id] = true;
        });
      };
      const ownAppsLoaded = addAccountApps(
        owner,
        // The user document's ID is the uid, unless it was saved without one
        user.id === authUser.id
          ? appsRequest.ownApps
          : getUserApplicationsData(user.id),
      );
      ownAppsLoaded.catch(() => undefined);

      appStoreUnsubscribe = subscribe(
        (state: IGlobalState) =>
          state.user && state.selectedApp ? state.selectedApp : undefined,
        (appId?: string) => {
          if (!appId) {
            return;
          }

          subscribeToApp(appId);
        },
      );

      if (featureFlagsUnsubscribe) {
        featureFlagsUnsubscribe();
      }
      featureFlagsUnsubscribe = onGlobalFeatureFlagsUpdate((flags) => {
        update((draft) => {
          draft.globalFeatureFlags = flags;
        });
      });

      if (firebaseUserUnsubscribe) {
        firebaseUserUnsubscribe();
      }
      firebaseUserUnsubscribe = onUserUpdate(user.id, (updatedUser) => {
        const oldUser = store.getRawState().user;
        update((draft) => {
          const nextUser = { ...oldUser, ...updatedUser };
          draft.user = nextUser;

          const owner = {
            id: nextUser.id,
            label: getAccountDisplayName(nextUser),
            isOwner: true,
          };
          Object.entries(draft.appToUser).forEach(([appId, appUser]) => {
            if (appUser.isOwner) {
              draft.appToUser[appId] = owner;
            }
          });
        });
      });

      invitedUsersUnsubscribe = onInvitedUsersUpdate(user.id, (invitedUsers) =>
        update((draft) => {
          draft.invitedUsers.accountsById[user.id] = invitedUsers;
          draft.invitedUsers.loadingAccountIds[user.id] = false;
          if (!draft.manageUsers.activeAccountId) {
            draft.manageUsers.activeAccountId = user.id;
          }
        }),
      );

      // Pages render from here on, and each account's apps show as they load
      update((draft) => {
        draft.userDataLoaded = true;
      });

      const { acceptedUsers, appsByAccount } = await appsRequest.teams;
      if (lastUser !== authUser) return;
      update((draft) => {
        draft.acceptedUsers = acceptedUsers;
      });

      const appResults = await Promise.allSettled([
        ownAppsLoaded,
        ...acceptedUsers.map((acceptedUser) =>
          addAccountApps(
            {
              label: getAccountDisplayName(acceptedUser),
              id: acceptedUser.id,
              isOwner: false,
            },
            appsByAccount[acceptedUser.id],
          ),
        ),
      ]);
      if (lastUser !== authUser) return;

      update((draft) => {
        draft.appsLoaded = true;
        // A failed account lookup must not be presented as a missing invitation.
        draft.applicationLoadFailed = appResults.some((result) => {
          if (result.status !== 'rejected') return false;
          return !isAccessDenied(result.reason);
        });
      });

      acceptedUsersUnsubscribe = onAcceptedUsersUpdate(
        user.id,
        (updatedAcceptedUsers, snapshot) => {
          update((draft) => {
            draft.acceptedUsers = updatedAcceptedUsers;

            const delegateAccountIds = new Set(
              updatedAcceptedUsers.map((acceptedUser) => acceptedUser.id),
            );

            Object.keys(draft.invitedUsers.accountsById).forEach(
              (accountId) => {
                if (accountId === user.id) return;
                if (!delegateAccountIds.has(accountId)) {
                  delete draft.invitedUsers.accountsById[accountId];
                  delete draft.invitedUsers.loadingAccountIds[accountId];
                }
              },
            );

            updatedAcceptedUsers.forEach((acceptedUser) => {
              if (
                typeof draft.invitedUsers.loadingAccountIds[acceptedUser.id] ===
                'undefined'
              ) {
                draft.invitedUsers.loadingAccountIds[acceptedUser.id] = false;
              }
            });

            Object.entries(draft.appToUser).forEach(([appId, appUser]) => {
              if (appUser.isOwner) return;

              const acceptedUser = updatedAcceptedUsers.find(
                (item) => item.id === appUser.id,
              );
              if (acceptedUser) {
                draft.appToUser[appId] = {
                  ...appUser,
                  label: getAccountDisplayName(acceptedUser),
                };
              }
            });
          });

          snapshot?.docChanges().forEach(async (change) => {
            try {
              if (change.type === 'added') {
                const { id, firstName } = change.doc.data();
                if (accountsWithApps.has(id)) return;
                const acceptedUser = change.doc.data();
                const addedAppUser = {
                  id,
                  isOwner: false,
                  label: getAccountDisplayName(acceptedUser) || firstName || id,
                };
                const addedUserApps = await getUserApplicationsData(id);

                addApps(addedUserApps, addedAppUser);
                accountsWithApps.add(id);
                update((draft) => {
                  draft.accountAppsLoaded[id] = true;
                });
              } else if (change.type === 'removed') {
                const teamId = change.doc.id;
                accountsWithApps.delete(teamId);
                // Access is already gone, so find the team's apps locally
                // rather than reading them again.
                const { appToUser, selectedApp: currentSelectedApp } =
                  store.getRawState();
                const removedAppIds = Object.keys(appToUser).filter(
                  (appId) => appToUser[appId]?.id === teamId,
                );

                if (
                  currentSelectedApp &&
                  removedAppIds.includes(currentSelectedApp)
                ) {
                  history.push('/');
                }

                const leftThemselves = consumeLeftTeam(teamId);
                update((draft) => {
                  draft.apps = draft.apps.filter(
                    (app) => !removedAppIds.includes(app.id),
                  );
                  removedAppIds.forEach((appId) => {
                    delete draft.appToUser[appId];
                  });
                  if (
                    draft.selectedApp &&
                    removedAppIds.includes(draft.selectedApp)
                  ) {
                    draft.selectedApp = draft.apps[0]?.id ?? null;
                  }
                  if (!leftThemselves) {
                    draft.removedTeams.push({
                      id: teamId,
                      name: getAccountDisplayName(change.doc.data()) || teamId,
                    });
                  }
                });
              }
            } catch (err) {
              console.error('err', err);
            }
          });
        },
      );
    }

    update((draft) => {
      draft.userDataLoaded = true;
      draft.appsLoaded = true;
      draft.globalLoading = false;
    });
  };

  authUnsubscribe = onUserAuth((authUser) =>
    handleUserAuth(authUser).catch((error) => {
      // A newer auth state owns the store now.
      if (lastUser !== authUser) return;
      handleAuthInitError(error);
      update((draft) => {
        draft.userDataLoaded = true;
        draft.appsLoaded = true;
        draft.globalLoading = false;
      });
    }),
  );
};

export const unsubscribeOnUserAuth = () => authUnsubscribe?.();

type ListAccountInvitesResponse = {
  invitedUsers: UserIHaveSentInviteTo[];
};
export const loadAccountInvites = async (
  accountId: string,
): Promise<ListAccountInvitesResponse | null> => {
  if (!accountId) {
    return null;
  }

  update((draft) => {
    draft.invitedUsers.loadingAccountIds[accountId] = true;
  });

  try {
    const listAccountInvitesCallable =
      callFirebaseFunction('listAccountInvites');
    const { data } = await listAccountInvitesCallable({ accountId });
    const payload = data as ListAccountInvitesResponse;

    if (!payload) {
      update((draft) => {
        draft.invitedUsers.loadingAccountIds[accountId] = false;
      });
      return null;
    }

    update((draft) => {
      draft.invitedUsers.accountsById[accountId] = payload.invitedUsers;
      draft.invitedUsers.loadingAccountIds[accountId] = false;
    });

    return payload;
  } catch (error) {
    update((draft) => {
      draft.invitedUsers.loadingAccountIds[accountId] = false;
    });
    throw error;
  }
};

export const setManageUsersActiveAccount = (accountId: string | null) => {
  update((draft) => {
    draft.manageUsers.activeAccountId = accountId;
  });
};

export const impersonateUser = async (
  email: string,
  authentication: unknown,
) => {
  const loginAdmin = callFirebaseFunction('loginAdmin');
  const { data } = await loginAdmin({ email, authentication });

  // make sure to reset all state and sign out before impersonating a user
  // this is just an extra safe way of making sure state is separated between accounts
  resetAllState();
  analyticsReset();
  await signOut();
  const auth = getAuth();
  await signInWithCustomToken(auth, (data as { token: string }).token);
};
